import React, { useState, useMemo } from 'react';
import { ChevronRight, ArrowLeft, Sparkles } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useEvent } from '../context/EventContext';
import { ImagePlaceholder } from '../components/ImagePlaceholder';
import { loc, pick } from '../lib/i18nContent';

const EDITORIAL_PREFIXES = ['EDITORIAL', 'VALIDATION', 'ВАЛИДАЦ', 'ТРЕБ', 'NEPIECIE', 'REDAKC'];
const isEditorial = (p) => EDITORIAL_PREFIXES.some((x) => p.toUpperCase().startsWith(x));
const GROUPS = [['house', 'dgroup_house'], ['craft', 'dgroup_craft'], ['tonight', 'dgroup_tonight']];

function Card({ c, lang, onOpen }) {
  const { t } = useLang();
  const GLABEL = { house: 'dgroup_house', craft: 'dgroup_craft', tonight: 'dgroup_tonight' };
  const catLabel = c.group ? t(GLABEL[c.group]) : c.category;
  return (
    <button
      data-testid={`discover-card-${c.slug}`}
      onClick={() => onOpen(c)}
      className="w-full text-left bg-[#111A38] border border-[#D4AF37]/20 rounded-2xl overflow-hidden active:scale-[0.99] transition-transform"
    >
      <ImagePlaceholder ratio="aspect-[16/9]" src={c.image_url} position={c.image_position} className="rounded-none border-0" label="Image — supplied later" />
      <div className="p-4 flex justify-between items-center gap-3">
        <div>
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#E5C158]/70">{catLabel}</span>
          <h3 className="font-serif text-xl text-[#FDFBF7] leading-tight">{loc(c, lang, 'title')}</h3>
          <p className="font-sans text-sm text-slate-400 mt-0.5">{loc(c, lang, 'excerpt')}</p>
        </div>
        <ChevronRight size={20} className="text-[#D4AF37] shrink-0" />
      </div>
    </button>
  );
}

export default function Discover() {
  const { t, lang } = useLang();
  const { config } = useEvent();
  const cards = config?.discover || [];
  const facts = config?.settings?.did_you_know || [];
  const [open, setOpen] = useState(null);

  const fact = useMemo(() => (facts.length ? pick(facts[Math.floor(Math.random() * facts.length)], lang) : null), [facts, lang]);

  if (open) {
    const body = loc(open, lang, 'body');
    return (
      <div data-testid="discover-article">
        <button onClick={() => setOpen(null)} data-testid="discover-back" className="flex items-center gap-1 text-sm text-[#E5C158] mb-4">
          <ArrowLeft size={16} /> {t('discover_title')}
        </button>
        <ImagePlaceholder ratio="aspect-[16/10]" src={open.image_url} position={open.image_position} label="Image — supplied later" />
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#E5C158]/70 mt-4 block">{open.category}</span>
        <h1 className="font-serif font-bold text-3xl text-[#FDFBF7] leading-tight mt-1">{loc(open, lang, 'title')}</h1>
        <div className="mt-4 space-y-3">
          {body.split('\n\n').map((para, i) => (
            <p key={i} className={`font-sans leading-relaxed ${isEditorial(para) ? 'text-slate-500 italic text-xs border-l-2 border-[#D4AF37]/30 pl-3' : 'text-slate-200 text-[15px]'}`}>{para}</p>
          ))}
        </div>
      </div>
    );
  }

  const grouped = GROUPS.map(([g, label]) => ({ label, items: cards.filter((c) => c.group === g) })).filter((x) => x.items.length);
  const ungrouped = cards.filter((c) => !c.group);

  return (
    <div data-testid="discover-screen">
      <h1 className="font-serif font-bold text-3xl text-[#FDFBF7]">{t('discover_title')}</h1>

      {fact && (
        <div data-testid="did-you-know" className="mt-4 flex gap-3 items-start bg-[#111A38] border-l-2 border-[#D4AF37] rounded-r-lg px-4 py-3">
          <Sparkles size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
          <div>
            <span className="font-mono text-[9px] uppercase tracking-widest text-[#D4AF37]/70">{t('didYouKnow')}</span>
            <p className="text-sm text-slate-200 mt-0.5 leading-snug">{fact}</p>
          </div>
        </div>
      )}

      {grouped.length > 0 ? grouped.map((sec) => (
        <div key={sec.label} className="mt-6">
          <h2 className="font-mono text-[11px] tracking-widest uppercase text-[#E5C158] mb-3">{t(sec.label)}</h2>
          <div className="space-y-4">{sec.items.map((c) => <Card key={c.id} c={c} lang={lang} onOpen={setOpen} />)}</div>
        </div>
      )) : null}

      {ungrouped.length > 0 && (
        <div className="mt-6 space-y-4">{ungrouped.map((c) => <Card key={c.id} c={c} lang={lang} onOpen={setOpen} />)}</div>
      )}
    </div>
  );
}
